import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Library, ExternalLink, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { reportApi } from '@/api/report.api';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { SavedViewBar } from '@/components/reports/SavedViewBar';
import { AccountSearchInput } from '@/components/reports/AccountSearchInput';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { CostCentreSelect } from '@/components/reports/CostCentreSelect';
import { FinancialPeriodSelect } from '@/components/reports/FinancialPeriodSelect';
import { ReportPrintPreview } from '@/components/reports/ReportPrintPreview';
import { ReportHeader } from '@/components/master/report/ReportHeader';
import { ReportFooter } from '@/components/master/report/ReportFooter';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import type {
  AccountLedgerDto, AccountLedgerRow, GeneralLedgerDto, GeneralLedgerFilter,
} from '@/types/report.types';

// ── helpers ────────────────────────────────────────────────────────────────────

function today() {
  return new Date().toISOString().split('T')[0];
}
function firstOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
}
function fmtAmt(n: number) {
  if (n === 0) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtBalance(n: number): string {
  if (n === 0) return '0.00';
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${abs} ${n > 0 ? 'Dr' : 'Cr'}`;
}
function fmtDate(iso: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso ?? '—';
  }
}

const PAGE_SIZE = 25;

const INPUT_CLS =
  'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500';
const LABEL_CLS = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';
const TH_CLS = 'px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide';
const TH_R_CLS = 'px-3 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide';
const AMT_CLS = 'px-3 py-2 text-sm text-right tabular-nums';

const ACCOUNT_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'Asset', label: 'Asset' },
  { value: 'Liability', label: 'Liability' },
  { value: 'Equity', label: 'Equity' },
  { value: 'Revenue', label: 'Revenue' },
  { value: 'Expense', label: 'Expense' },
];

// ── Row styling by rowType ────────────────────────────────────────────────────

function rowClass(rowType: AccountLedgerRow['rowType']): string {
  switch (rowType) {
    case 'OPENING':
    case 'CLOSING':
      return 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 font-semibold';
    case 'SUBTOTAL':
      return 'bg-gray-50 dark:bg-gray-800/60 italic text-gray-600 dark:text-gray-400';
    case 'UNPOSTED':
      return 'bg-gray-50/70 dark:bg-gray-800/40 italic text-gray-400 dark:text-gray-500';
    case 'UNPOSTED_TOTAL':
      return 'bg-gray-100 dark:bg-gray-800/70 font-bold text-gray-500 dark:text-gray-400';
    default:
      return 'hover:bg-gray-50 dark:hover:bg-gray-800/50';
  }
}

function rowDateLabel(row: AccountLedgerRow): string {
  if (row.rowType === 'OPENING') return 'Opening';
  if (row.rowType === 'CLOSING') return 'Closing';
  if (row.rowType === 'SUBTOTAL') return '';
  if (row.rowType === 'UNPOSTED_TOTAL') return '';
  return fmtDate(row.documentDate);
}

// ── Excel / CSV export (flattened sections) ───────────────────────────────────

const EXPORT_COLUMNS: ExcelColumn[] = [
  { key: 'Account', header: 'Account', width: 34 },
  { key: 'Date', header: 'Date', width: 14 },
  { key: 'JournalNo', header: 'Journal No', width: 18 },
  { key: 'Type', header: 'Type', width: 10 },
  { key: 'Reference', header: 'Reference', width: 20 },
  { key: 'Narration', header: 'Narration', width: 36 },
  { key: 'Debit', header: 'Debit', width: 14, numFmt: '#,##0.00' },
  { key: 'Credit', header: 'Credit', width: 14, numFmt: '#,##0.00' },
  { key: 'Balance', header: 'Running Balance', width: 16, numFmt: '#,##0.00' },
];

function buildExportRows(data: GeneralLedgerDto): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];
  for (const section of data.sections) {
    // Section separator/header
    rows.push({
      Account: `${section.accountCode} — ${section.accountName} (${section.accountType})`,
      Date: '', JournalNo: '', Type: '', Reference: '',
      Narration: `Opening balance: ${fmtBalance(section.openingBalance)}`,
      Debit: '', Credit: '', Balance: '',
    });
    for (const r of section.rows) {
      rows.push({
        Account: '',
        Date: r.rowType === 'OPENING' ? 'Opening'
          : r.rowType === 'CLOSING' ? 'Closing'
          : r.rowType === 'SUBTOTAL' ? 'Subtotal'
          : r.rowType === 'UNPOSTED_TOTAL' ? 'Unposted total'
          : (r.documentDate ?? ''),
        JournalNo: r.rowType === 'OPENING' ? 'B/F'
          : r.rowType === 'CLOSING' ? 'C/F'
          : (r.documentNumber ?? ''),
        Type: r.transactionTypeCode ?? '',
        Reference: r.reference ?? '',
        Narration: (r.rowType === 'UNPOSTED' ? '[UNPOSTED] ' : '') + (r.narration ?? ''),
        Debit: r.debit,
        Credit: r.credit,
        Balance: r.runningBalance,
      });
    }
    // Section footer + blank separator
    rows.push({
      Account: '', Date: '', JournalNo: '', Type: '', Reference: '',
      Narration: `TOTAL — ${section.accountCode}`,
      Debit: section.totalDebit, Credit: section.totalCredit, Balance: section.closingBalance,
    });
    rows.push({ Account: '', Date: '', JournalNo: '', Type: '', Reference: '', Narration: '', Debit: '', Credit: '', Balance: '' });
  }
  return rows;
}

// ── Component ──────────────────────────────────────────────────────────────────

const DEFAULT_FILTER: GeneralLedgerFilter = {
  fromDate: firstOfMonth(),
  toDate: today(),
  monthlySubtotals: false,
  includeUnposted: false,
  includeZeroActivity: false,
  pageNumber: 1,
  pageSize: PAGE_SIZE,
};

export const GeneralLedgerPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { user } = useAuth();

  const [filter, setFilter] = useState<GeneralLedgerFilter>({ ...DEFAULT_FILTER });
  const [accountLabel, setAccountLabel] = useState('');
  const [data, setData] = useState<GeneralLedgerDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrint, setShowPrint] = useState(false);

  const run = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportApi.getGeneralLedger({ ...filter, pageNumber: page, pageSize: PAGE_SIZE });
      setData(res.data?.data ?? null);
      setFilter(f => ({ ...f, pageNumber: page }));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load general ledger');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const resetFilter = () => {
    setFilter({ ...DEFAULT_FILTER });
    setAccountLabel('');
    setData(null);
    setError(null);
  };

  if (!hasPermission(Permissions.RPT_LEDGER)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const currentPage = data?.pageNumber ?? filter.pageNumber ?? 1;
  const totalPages = data ? Math.max(1, Math.ceil(data.totalAccounts / (data.pageSize || PAGE_SIZE))) : 0;
  const rowCount = data?.sections.reduce((s, sec) => s + sec.rows.length, 0) ?? 0;
  const filterSummary = `${filter.fromDate}..${filter.toDate} · page ${currentPage}`;

  const handleExcel = () => {
    if (!data) return;
    exportToExcel({
      sheetName: 'General Ledger',
      columns: EXPORT_COLUMNS,
      rows: buildExportRows(data),
      title: 'General Ledger',
      subtitle: `${data.companyName} · ${filter.fromDate} to ${filter.toDate} · Page ${currentPage} of ${totalPages}`,
    }, `GeneralLedger_${filter.fromDate}_${filter.toDate}_p${currentPage}`);
  };

  const handleCsv = () => {
    if (!data) return;
    exportToCsvRows(
      EXPORT_COLUMNS,
      buildExportRows(data),
      `GeneralLedger_${filter.fromDate}_${filter.toDate}_p${currentPage}`,
    );
  };

  const applySavedView = (f: unknown) => {
    if (!f || typeof f !== 'object') return;
    setFilter(prev => ({ ...prev, ...(f as Partial<GeneralLedgerFilter>), pageNumber: 1 }));
  };

  const renderSection = (section: AccountLedgerDto) => (
    <div
      key={section.accountId}
      className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
      style={{ breakInside: 'avoid' }}
    >
      {/* Section header */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 bg-gray-50 dark:bg-gray-800/70 border-b border-gray-200 dark:border-gray-700">
        <div>
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            {section.accountCode} — {section.accountName}
          </span>
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{section.accountType}</span>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Opening: <span className="font-semibold text-gray-800 dark:text-gray-200 tabular-nums">{fmtBalance(section.openingBalance)}</span>
        </div>
      </div>

      {/* Rows */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse min-w-[900px]">
          <thead>
            <tr className="bg-gray-50/60 dark:bg-gray-800/40">
              <th className={cn(TH_CLS, 'w-28')}>Date</th>
              <th className={cn(TH_CLS, 'w-32')}>Journal No</th>
              <th className={cn(TH_CLS, 'w-16')}>Type</th>
              <th className={cn(TH_CLS, 'w-28')}>Reference</th>
              <th className={TH_CLS}>Narration</th>
              <th className={cn(TH_R_CLS, 'w-28')}>Debit</th>
              <th className={cn(TH_R_CLS, 'w-28')}>Credit</th>
              <th className={cn(TH_R_CLS, 'w-32')}>Running Balance</th>
            </tr>
          </thead>
          <tbody>
            {section.rows.map((row, idx) => (
              <tr
                key={`${row.journalId ?? row.rowType}-${idx}`}
                className={cn('border-t border-gray-100 dark:border-gray-800', rowClass(row.rowType))}
              >
                <td className="px-3 py-1.5 text-sm whitespace-nowrap">{rowDateLabel(row)}</td>
                <td className="px-3 py-1.5 text-sm">
                  {row.journalId && row.documentNumber ? (
                    <button
                      onClick={() => navigate(AppRoutes.GL_JOURNAL_DETAIL.replace(':id', row.journalId!))}
                      className="text-brand-600 dark:text-brand-400 hover:underline flex items-center gap-1"
                    >
                      {row.documentNumber}
                      <ExternalLink size={11} />
                    </button>
                  ) : (
                    <span className="italic">
                      {row.rowType === 'OPENING' ? 'B/F'
                        : row.rowType === 'CLOSING' ? 'C/F'
                        : row.rowType === 'SUBTOTAL' ? (row.narration ?? 'Subtotal')
                        : row.rowType === 'UNPOSTED_TOTAL' ? 'Unposted total'
                        : (row.documentNumber ?? '—')}
                    </span>
                  )}
                  {row.rowType === 'UNPOSTED' && (
                    <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium not-italic bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                      unposted
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-xs">{row.transactionTypeCode ?? '—'}</td>
                <td className="px-3 py-1.5 text-sm">{row.reference ?? '—'}</td>
                <td className="px-3 py-1.5 text-sm max-w-xs truncate" title={row.narration ?? ''}>
                  {(row.rowType === 'SUBTOTAL' || row.rowType === 'UNPOSTED_TOTAL') && !row.journalId
                    ? '' : (row.narration ?? '—')}
                </td>
                <td className={AMT_CLS.replace('py-2', 'py-1.5')}>{row.debit !== 0 ? fmtAmt(row.debit) : '—'}</td>
                <td className={AMT_CLS.replace('py-2', 'py-1.5')}>{row.credit !== 0 ? fmtAmt(row.credit) : '—'}</td>
                <td className={cn(AMT_CLS.replace('py-2', 'py-1.5'), 'font-medium')}>
                  {row.rowType === 'SUBTOTAL' && row.runningBalance === 0 ? '—' : fmtBalance(row.runningBalance)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Section footer */}
      <div className="flex flex-wrap items-center justify-end gap-6 px-4 py-2 bg-gray-50 dark:bg-gray-800/70 border-t border-gray-200 dark:border-gray-700 text-xs">
        <span className="text-gray-500 dark:text-gray-400">
          Total Dr: <span className="font-semibold text-gray-900 dark:text-white tabular-nums">{fmtAmt(section.totalDebit)}</span>
        </span>
        <span className="text-gray-500 dark:text-gray-400">
          Total Cr: <span className="font-semibold text-gray-900 dark:text-white tabular-nums">{fmtAmt(section.totalCredit)}</span>
        </span>
        <span className="text-gray-500 dark:text-gray-400">
          Closing: <span className="font-semibold text-gray-900 dark:text-white tabular-nums">{fmtBalance(section.closingBalance)}</span>
        </span>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Library size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">General Ledger</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          onPrint={() => setShowPrint(true)}
          hasData={!!data}
          reportKey="GENERAL_LEDGER"
          rowCount={rowCount}
          filterSummary={filterSummary}
        />
      </div>

      {/* Saved views */}
      <SavedViewBar
        reportKey="GENERAL_LEDGER"
        currentFilters={filter}
        onApply={applySavedView}
      />

      {/* Filter Panel */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        {/* Row 1 — dates + quick period */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS}>From Date *</label>
            <input
              type="date"
              value={filter.fromDate}
              onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>To Date *</label>
            <input
              type="date"
              value={filter.toDate}
              onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Quick Period</label>
            <FinancialPeriodSelect
              onPeriodChange={(from, to) => {
                setFilter(f => ({ ...f, fromDate: from, toDate: to }));
              }}
            />
          </div>
        </div>

        {/* Row 2 — account, code range, type */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className={LABEL_CLS}>Account (optional)</label>
            <AccountSearchInput
              accountId={filter.accountId}
              accountLabel={accountLabel}
              onChange={(id, code, name) => {
                setFilter(f => ({ ...f, accountId: id }));
                setAccountLabel(`${code} — ${name}`);
              }}
              onClear={() => {
                setFilter(f => ({ ...f, accountId: undefined }));
                setAccountLabel('');
              }}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Account Code From</label>
            <input
              type="text"
              value={filter.accountCodeFrom ?? ''}
              placeholder="e.g. 1000"
              onChange={e => setFilter(f => ({ ...f, accountCodeFrom: e.target.value || undefined }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Account Code To</label>
            <input
              type="text"
              value={filter.accountCodeTo ?? ''}
              placeholder="e.g. 9999"
              onChange={e => setFilter(f => ({ ...f, accountCodeTo: e.target.value || undefined }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Account Type</label>
            <select
              value={filter.accountTypeFilter ?? ''}
              onChange={e => setFilter(f => ({ ...f, accountTypeFilter: e.target.value || undefined }))}
              className={INPUT_CLS}
            >
              {ACCOUNT_TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 3 — location, cost centre */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS}>Location</label>
            <LocationSelect
              value={filter.locationId}
              onChange={id => setFilter(f => ({ ...f, locationId: id ?? undefined }))}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Cost Centre</label>
            <CostCentreSelect
              value={filter.costCenterId}
              onChange={id => setFilter(f => ({ ...f, costCenterId: id ?? undefined }))}
            />
          </div>
        </div>

        {/* Checkboxes + actions */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={filter.monthlySubtotals ?? false}
                onChange={e => setFilter(f => ({ ...f, monthlySubtotals: e.target.checked }))}
                className="rounded"
              />
              Monthly Subtotals
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={filter.includeUnposted ?? false}
                onChange={e => setFilter(f => ({ ...f, includeUnposted: e.target.checked }))}
                className="rounded"
              />
              Include Unposted (separate)
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={filter.includeZeroActivity ?? false}
                onChange={e => setFilter(f => ({ ...f, includeZeroActivity: e.target.checked }))}
                className="rounded"
              />
              Include Zero-Activity
            </label>
          </div>
          <div className="flex items-center gap-2">
            <RunReportButton
              onClick={() => run(1)}
              isLoading={loading}
              disabled={!filter.fromDate || !filter.toDate}
            />
            <button
              onClick={resetFilter}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <RotateCcw size={14} />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {/* Results */}
      {data && (
        <div className="flex flex-col gap-4">
          {/* Grand totals card */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                General Ledger — {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
                {' · '}{data.totalAccounts.toLocaleString('en-US')} accounts
                {' · '}Generated {fmtDate(data.generatedAt)}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Grand Total Debit</div>
                <div className="font-bold text-gray-900 dark:text-white tabular-nums">{fmtAmt(data.grandTotalDebit)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Grand Total Credit</div>
                <div className="font-bold text-gray-900 dark:text-white tabular-nums">{fmtAmt(data.grandTotalCredit)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Accounts on Page</div>
                <div className="font-bold text-gray-900 dark:text-white tabular-nums">{data.sections.length}</div>
              </div>
            </div>
          </div>

          {/* Sections */}
          {data.sections.length === 0 ? (
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-600">
              No accounts with activity match the selected criteria.
            </div>
          ) : (
            <div data-print-section id="report-print-area" className="flex flex-col gap-4">
              {data.sections.map(renderSection)}
            </div>
          )}

          {/* Pagination — by account */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Accounts {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, data.totalAccounts)} of {data.totalAccounts.toLocaleString('en-US')}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => run(currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={14} />
                  Prev
                </button>
                <span className="px-2 text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => run(currentPage + 1)}
                  disabled={currentPage >= totalPages || loading}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!data && !loading && (
        <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm">
          Set the date range and click "Run Report" to generate the General Ledger.
        </div>
      )}

      {/* ── Print Preview ─────────────────────────────────────────────────── */}
      {data && (
        <ReportPrintPreview
          isOpen={showPrint}
          onClose={() => setShowPrint(false)}
          title="General Ledger"
          orientation="landscape"
        >
          <ReportHeader
            reportTitle="General Ledger"
            printedBy={user?.fullName}
            printedAt={new Date()}
            meta={[
              { label: 'Company', value: data.companyName },
              { label: 'Period',  value: `${fmtDate(data.fromDate)} to ${fmtDate(data.toDate)}` },
              { label: 'Accounts', value: `${data.sections.length} shown (of ${data.totalAccounts} total)` },
              { label: 'Page',    value: `${currentPage} of ${totalPages}` },
            ]}
          />
          {data.sections.map(section => (
            <div key={section.accountId} style={{ breakInside: 'avoid', marginTop: '10px' }}>
              <div style={{ fontSize: '9.5px', fontWeight: 700, padding: '4px 0', borderBottom: '1px solid #1e3a5f' }}>
                {section.accountCode} — {section.accountName}
                <span style={{ fontWeight: 400, color: '#6b7280' }}> · {section.accountType} · Opening {fmtBalance(section.openingBalance)}</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8px', marginTop: '2px' }}>
                <thead>
                  <tr>
                    {['Date','Journal No','Type','Reference','Narration','Debit','Credit','Balance'].map((h, i) => (
                      <th key={h} style={{ background: '#1e3a5f', color: 'white', padding: '3px 5px', textAlign: i >= 5 ? 'right' : 'left', fontWeight: 700, whiteSpace: 'nowrap', fontSize: '8px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((row, idx) => {
                    const isSpecial = row.rowType !== 'TRANSACTION' && row.rowType !== 'UNPOSTED';
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', background: isSpecial ? '#eff6ff' : row.rowType === 'UNPOSTED' ? '#f9fafb' : undefined, fontStyle: row.rowType === 'UNPOSTED' || row.rowType === 'SUBTOTAL' ? 'italic' : undefined, color: row.rowType === 'UNPOSTED' ? '#9ca3af' : undefined }}>
                        <td style={{ padding: '2px 5px', whiteSpace: 'nowrap' }}>{rowDateLabel(row)}</td>
                        <td style={{ padding: '2px 5px', fontFamily: 'monospace' }}>
                          {row.rowType === 'OPENING' ? 'B/F' : row.rowType === 'CLOSING' ? 'C/F' : (row.documentNumber ?? (row.rowType === 'SUBTOTAL' ? 'Subtotal' : row.rowType === 'UNPOSTED_TOTAL' ? 'Unposted total' : '—'))}
                        </td>
                        <td style={{ padding: '2px 5px', color: '#6b7280' }}>{row.transactionTypeCode ?? '—'}</td>
                        <td style={{ padding: '2px 5px', color: '#6b7280' }}>{row.reference ?? '—'}</td>
                        <td style={{ padding: '2px 5px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.narration ?? '—'}</td>
                        <td style={{ padding: '2px 5px', textAlign: 'right', fontFamily: 'monospace' }}>{row.debit !== 0 ? fmtAmt(row.debit) : '—'}</td>
                        <td style={{ padding: '2px 5px', textAlign: 'right', fontFamily: 'monospace' }}>{row.credit !== 0 ? fmtAmt(row.credit) : '—'}</td>
                        <td style={{ padding: '2px 5px', textAlign: 'right', fontFamily: 'monospace', fontWeight: isSpecial ? 700 : 400 }}>{fmtBalance(row.runningBalance)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '1px solid #1e3a5f', background: '#f9fafb' }}>
                    <td colSpan={5} style={{ padding: '3px 5px', fontWeight: 700 }}>TOTAL — {section.accountCode}</td>
                    <td style={{ padding: '3px 5px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmtAmt(section.totalDebit)}</td>
                    <td style={{ padding: '3px 5px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmtAmt(section.totalCredit)}</td>
                    <td style={{ padding: '3px 5px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmtBalance(section.closingBalance)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ))}
          <ReportFooter />
        </ReportPrintPreview>
      )}
    </div>
  );
};
